// packages/utils/src/shared/site-hostnames/normalizeSiteHostname.ts
//
// Two normalisations of a hostname for the hosted sites (docs/website-builder/README.md §8.1,
// design/domains-and-edge.md §2.5), kept apart because they answer different questions:
//
// - `normalizeHostHeader`: the `Host` of a request. Lower-cased, port and trailing dot removed,
//   then it must be `^[a-z0-9.-]{1,253}$` or the request is refused with 400 (step 1). The tenant
//   comes from this value only; `x-forwarded-host` is never read (D15).
// - `normalizeSiteHostnameInput`: what a business types on Website → Domain. Scheme, path, port
//   and trailing dot are stripped, an internationalised name becomes punycode, and names that can
//   never be a business's own domain (IP literals, single labels, TourTask's own domains, the
//   sites zone, reserved TLDs) are refused.
import { isHostAtOrUnder } from '../net/hostRules';

/** A hostname as it may appear in a `Host` header once normalised. */
const HOST_HEADER_PATTERN = /^[a-z0-9.-]{1,253}$/;

/** A port at the end of a `Host` header. */
const HOST_PORT_SUFFIX = /:\d{1,5}$/;

/** The hosts that mean "this machine": the deploy's smoke and the fixture site use them. */
const LOOPBACK_HOSTNAMES: ReadonlySet<string> = new Set(['localhost', '127.0.0.1']);

/**
 * The hostname of a `Host` header: lower-cased, without the port and a trailing dot. `null` when
 * the header is absent or is not `^[a-z0-9.-]{1,253}$` afterwards (an IPv6 literal included).
 */
export function normalizeHostHeader(hostHeader: string | null | undefined): string | null {
  if (hostHeader === null || hostHeader === undefined) return null;
  let host = hostHeader.trim().toLowerCase().replace(HOST_PORT_SUFFIX, '');
  if (host.endsWith('.')) host = host.slice(0, -1);
  return HOST_HEADER_PATTERN.test(host) ? host : null;
}

/** Whether a normalised hostname is this machine (`localhost` or `127.0.0.1`). */
export function isLoopbackHostname(hostname: string): boolean {
  return LOOPBACK_HOSTNAMES.has(hostname);
}

/** One DNS label: letters, digits and inner hyphens, at most 63 characters (punycode included). */
const DNS_LABEL_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** A dotted IPv4 literal. */
const IPV4_LITERAL_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Names that are never a business's own domain: TourTask's own, and the reserved or special-use
 * TLDs (RFC 2606, RFC 6761, RFC 7686, `.arpa`). The sites zone is added by the caller.
 */
const BLOCKED_DOMAIN_SUFFIXES = [
  'tourtask.com',
  'tourtask.dev',
  'localhost',
  'local',
  'test',
  'example',
  'invalid',
  'internal',
  'onion',
  'arpa',
] as const;

export type SiteHostnameInputRefusal = 'empty' | 'invalid' | 'blocked';
export type SiteHostnameInputResult =
  | { ok: true; hostname: string }
  | { ok: false; reason: SiteHostnameInputRefusal };

/**
 * A domain typed by a business, as the lowercase ASCII (punycode) FQDN stored in
 * `site_hostnames.hostname`. `zone` is the sites zone (`sitesZone()`), refused like TourTask's
 * own domains; `null` while no zone is configured.
 */
export function normalizeSiteHostnameInput(raw: string, zone: string | null): SiteHostnameInputResult {
  let value = raw.trim().toLowerCase();
  if (value === '') return { ok: false, reason: 'empty' };

  value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  const end = value.search(/[/?#]/);
  if (end !== -1) value = value.slice(0, end);
  if (value.includes('@') || value.startsWith('[')) return { ok: false, reason: 'invalid' };
  value = value.replace(HOST_PORT_SUFFIX, '');
  if (value.endsWith('.')) value = value.slice(0, -1);
  if (value === '') return { ok: false, reason: 'empty' };

  // The WHATWG URL parser applies IDNA (UTS #46), so an internationalised name becomes its
  // punycode form in Node and in the browser alike; anything it cannot parse as a host is invalid.
  let ascii: string;
  try {
    ascii = new URL(`http://${value}`).hostname;
  } catch {
    return { ok: false, reason: 'invalid' };
  }

  if (ascii.length > 253 || IPV4_LITERAL_PATTERN.test(ascii) || ascii.includes(':')) {
    return { ok: false, reason: 'invalid' };
  }
  const labels = ascii.split('.');
  if (labels.length < 2 || !labels.every((label) => DNS_LABEL_PATTERN.test(label))) {
    return { ok: false, reason: 'invalid' };
  }
  const blocked: readonly string[] = zone === null ? BLOCKED_DOMAIN_SUFFIXES : [...BLOCKED_DOMAIN_SUFFIXES, zone];
  if (blocked.some((suffix) => isHostAtOrUnder(ascii, suffix))) return { ok: false, reason: 'blocked' };
  return { ok: true, hostname: ascii };
}
